// Country selection service for managing user's selected country and city
import { authService } from '../config/supabase';
import { dataService } from './dataService';

const COUNTRY_STORAGE_KEY = 'selected_country';
const CITY_STORAGE_KEY = 'selected_city';

export const countryService = {
  // Get selected country - checks user profile first, then localStorage
  async getSelectedCountry() {
    try {
      // First, try to get from logged-in user's profile
      const currentUser = await authService.getCurrentUser();
      if (currentUser?.email) {
        const customerRecord = await dataService.getCustomerByEmail(currentUser.email).catch(() => null);
        if (customerRecord?.country) {
          // Also save to localStorage for consistency
          localStorage.setItem(COUNTRY_STORAGE_KEY, customerRecord.country);
          return customerRecord.country;
        }
      }
      
      // Fallback to localStorage (for non-logged-in users)
      return localStorage.getItem(COUNTRY_STORAGE_KEY);
    } catch (error) {
      console.error('Error getting selected country:', error);
      // Fallback to localStorage
      return localStorage.getItem(COUNTRY_STORAGE_KEY);
    }
  },

  // Get selected city - checks user profile first, then localStorage
  async getSelectedCity() {
    try {
      // First, try to get from logged-in user's profile
      const currentUser = await authService.getCurrentUser();
      if (currentUser?.email) {
        const customerRecord = await dataService.getCustomerByEmail(currentUser.email).catch(() => null);
        if (customerRecord?.city) {
          // Also save to localStorage for consistency
          localStorage.setItem(CITY_STORAGE_KEY, customerRecord.city);
          return customerRecord.city;
        }
      }
      
      // Fallback to localStorage (for non-logged-in users)
      return localStorage.getItem(CITY_STORAGE_KEY);
    } catch (error) {
      console.error('Error getting selected city:', error);
      // Fallback to localStorage
      return localStorage.getItem(CITY_STORAGE_KEY);
    }
  },

  // Set selected country - saves to user profile if logged in, and localStorage
  async setSelectedCountry(country) {
    try {
      // Save to localStorage first (works for both logged-in and non-logged-in users)
      localStorage.setItem(COUNTRY_STORAGE_KEY, country);
      
      // If user is logged in, also update their profile
      const currentUser = await authService.getCurrentUser();
      if (currentUser?.email) {
        try {
          await dataService.updateCustomerByEmail(currentUser.email, { country });
          console.log('Country updated in user profile:', country);
        } catch (error) {
          console.error('Error updating country in user profile:', error);
          // Don't fail if profile update fails, localStorage is already set
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error setting selected country:', error);
      return false;
    }
  },

  // Set selected city - saves to user profile if logged in, and localStorage
  async setSelectedCity(city) {
    try {
      // Save to localStorage first (works for both logged-in and non-logged-in users)
      localStorage.setItem(CITY_STORAGE_KEY, city);
      
      // If user is logged in, also update their profile
      const currentUser = await authService.getCurrentUser();
      if (currentUser?.email) {
        try {
          await dataService.updateCustomerByEmail(currentUser.email, { city });
          console.log('City updated in user profile:', city);
        } catch (error) {
          console.error('Error updating city in user profile:', error);
          // Don't fail if profile update fails, localStorage is already set
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error setting selected city:', error);
      return false;
    }
  },

  // Set both country and city
  async setLocation(country, city) {
    try {
      await this.setSelectedCountry(country);
      if (city) {
        await this.setSelectedCity(city);
      }
      return true;
    } catch (error) {
      console.error('Error setting location:', error);
      return false;
    }
  },

  // Check if country is selected
  async hasSelectedCountry() {
    const country = await this.getSelectedCountry();
    return !!country;
  },

  // Check if both country and city are selected
  async hasSelectedLocation() {
    const country = await this.getSelectedCountry();
    const city = await this.getSelectedCity();
    return !!(country && city);
  },

  // Clear selected country and city (useful for logout or reset)
  clearSelectedCountry() {
    localStorage.removeItem(COUNTRY_STORAGE_KEY);
    localStorage.removeItem(CITY_STORAGE_KEY);
  }
};

export default countryService;

